import Icon from '@/components/icons/Icon';
import { Dropdown } from '@/components/modules/dropdown';
import { _s, getLang } from '@/locale';
import moment from 'moment';
import { DateTimePickerInputProps, DateTimePickerManagerOutput } from './DateTimePicker.types';

import { Button } from '@/components/elements/forms/buttons';
import DatePicker from '@/features/date-picker/DatePicker';
import useDatePickerManager from '@/features/date-picker/DatePicker.hooks';
import { formatSelectedRange } from '@/features/date-picker/helpers';
import { classnames } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { TimeOfDayOption } from '@/types/state/search';
import { Fragment, useRef } from 'react';
import { twJoin } from 'tailwind-merge';
import { getTimeOfDateFilterLabel } from '../../pages/index.helpers';

const baseTranslationKey = 'features.searchV2.components.DateTimePicker';

const TIME_OF_DAY_OPTIONS: { key: TimeOfDayOption; label: string }[] = [
  {
    key: 'morning',
    label: `${getLang() === 'sv' ? 'Kl.' : ''} 06-12`,
  },
  {
    key: 'afternoon',
    label: `${getLang() === 'sv' ? 'Kl.' : ''} 12-18`,
  },
  {
    key: 'evening',
    label: `${getLang() === 'sv' ? 'Kl.' : ''} 18-22`,
  },
];

export const DATE_FORMAT = 'YYYY-MM-DD' as const;

const styles = `
.scroll-calendar {
  /* Fallback for older browsers (uses 100vh) */
  max-height: calc(50vh - env(safe-area-inset-bottom) - env(safe-area-inset-top));

  /* overrides the above if supported */
  max-height: calc(50dvh - env(safe-area-inset-bottom) - env(safe-area-inset-top));
}
`;

type DateTimePickerProps = DateTimePickerManagerOutput & {
  renderInputComponent: (inputProps: DateTimePickerInputProps) => JSX.Element;
  renderCalendarContainer?: ({ children }: { children: React.ReactNode }) => JSX.Element;
};

type TimeOfDayToggleSelectProps = {
  options: { key: TimeOfDayOption; label: string }[];
  value: string;
  onSelect: (value: string) => void;
};

const TimeOfDayToggleSelect = ({ options, value, onSelect }: TimeOfDayToggleSelectProps) => {
  const handleOnSelect = (event: React.MouseEvent<HTMLButtonElement>, value) => {
    event.preventDefault();
    onSelect(value);
  };
  return (
    <div className="no-scrollbar flex gap-2 overflow-x-scroll">
      {options.map((option) => {
        const isSelected = value === option.key;
        return (
          <button
            className={classnames(
              'px-sm py-xs text-m flex h-[40px] min-w-fit cursor-pointer items-center whitespace-nowrap rounded-full border outline-none',
              isSelected
                ? 'border-bg_secondary_selected bg-bg_secondary_selected text-white'
                : 'border-border_regular bg-bg_neutral',
            )}
            key={`select_key_${option.key}`}
            onClick={(e) => handleOnSelect(e, option.key)}>
            {option.label}
            {isSelected && <Icon variant="check" color="white" />}
          </button>
        );
      })}
    </div>
  );
};

const DateTimePicker = ({
  startDate,
  endDate,
  timeOfDay,
  showCalendar,
  onCloseCalendar,
  onOpenCalendar,
  onChangeDateTime,
  onDateTimeSearch,
  renderInputComponent,
  renderCalendarContainer,
}: DateTimePickerProps) => {
  const { isMobileView } = useMobileView();
  const calendarScrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedDateRange = startDate
    ? { start: moment.utc(startDate).toDate(), end: endDate ? moment.utc(endDate).toDate() : undefined }
    : undefined;

  const datePickerProps = useDatePickerManager({
    numberOfMonthsToShow: 6,
    defaultDateRange: selectedDateRange,
  });
  const { selectedRange } = datePickerProps;

  const hasFilterApplied = Boolean(startDate || endDate || timeOfDay);
  const value = startDate && endDate ? [moment.utc(startDate).toDate(), moment.utc(endDate).toDate()] : null;

  const handleOnDateSelect = (date: Date) => {
    let start = undefined;
    let end = undefined;

    if ((selectedRange.start && selectedRange.end) || (!selectedRange.start && !selectedRange.end)) {
      start = moment(date).format(DATE_FORMAT);
      end = undefined;
    } else if (selectedRange.start && !selectedRange.end) {
      start = moment(selectedRange.start).format(DATE_FORMAT);
      end = moment(date).format(DATE_FORMAT);
    }

    /**
     * Always make sure the end date is after the start date. otherwise switch them
     */
    if (start && end && moment(start).isAfter(end)) {
      [start, end] = [end, start];
    }

    datePickerProps.onDateSelect(date);
    onChangeDateTime({ startDate: start, endDate: end, timeOfDay });
  };

  const handleToggleTimeOfDay = (option: TimeOfDayOption) => {
    if (timeOfDay === option) {
      onChangeDateTime({ startDate, endDate, timeOfDay: null });
      return;
    }
    onChangeDateTime({ startDate, endDate, timeOfDay: option });
  };

  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.keyCode === 13) {
      onOpenCalendar();
    }
  };

  const handleOnClear = (_: React.MouseEvent<HTMLButtonElement>) => {
    datePickerProps.resetSelection();
  };

  const inputProps: DateTimePickerInputProps = {
    placeholder: _s('when'),
    value: formatSelectedRange(
      selectedRange.start && selectedRange.end
        ? [selectedRange.start, selectedRange.end]
        : selectedRange.start
        ? [selectedRange.start]
        : [],
      new Date(),
    ).concat(timeOfDay ? `, ${getTimeOfDateFilterLabel(timeOfDay)}` : ''),
    className: twJoin('ml-sm w-full text-l text-ellipsis bg-transparent', (hasFilterApplied || value) && 'pr-xl'),
    hasFilterApplied,
    readOnly: true,
    onClick: onOpenCalendar,
    onChange: (e) => e.preventDefault(),
    onKeyDown: handleOnKeyDown,
    onClear: handleOnClear,
  };

  const renderCalendar = () => {
    if (isMobileView) {
      return (
        <DatePicker
          {...datePickerProps}
          continuousScrollContainer={calendarScrollContainerRef}
          onDateSelect={handleOnDateSelect}
          type="continuous"
        />
      );
    }

    return <DatePicker {...datePickerProps} onDateSelect={handleOnDateSelect} type="paged" />;
  };

  const renderContainer = () => {
    return (
      <Fragment>
        {showCalendar && !isMobileView && (
          <Dropdown allowClick onClickOutside={onCloseCalendar} className="right-0 min-w-full py-0">
            <div className="p-md">
              {renderCalendar()}
              <div className="pt-md flex items-center justify-between">
                <div className="gap-x-sm flex items-center">
                  <span>{_s(`${baseTranslationKey}.ctaDescription`)}</span>
                  <TimeOfDayToggleSelect
                    onSelect={handleToggleTimeOfDay}
                    options={TIME_OF_DAY_OPTIONS}
                    value={timeOfDay}
                  />
                </div>

                <Button size="md" onMouseDown={onDateTimeSearch} leftIcon={<Icon variant="search" />}>
                  {_s('search')}
                </Button>
              </div>
            </div>
          </Dropdown>
        )}
        {showCalendar && isMobileView && (
          <Fragment>
            <style>{styles}</style>
            <div className="mt-xs mb-xl pt-xs drop-shadow-xs bg-surface_l2 rounded-lg">
              <div ref={calendarScrollContainerRef} className="px-md scroll-calendar overflow-y-auto">
                {renderCalendar()}
              </div>
              <div className="pl-md py-md border-border_disabled border-t">
                <TimeOfDayToggleSelect
                  onSelect={handleToggleTimeOfDay}
                  options={TIME_OF_DAY_OPTIONS}
                  value={timeOfDay}
                />
              </div>
            </div>
            <Button size="lg" block onMouseDown={onDateTimeSearch} leftIcon={<Icon variant="search" />}>
              {_s('search')}
            </Button>
          </Fragment>
        )}
      </Fragment>
    );
  };
  return (
    <div className={classnames('relative')}>
      {renderInputComponent(inputProps)}
      {renderCalendarContainer ? renderCalendarContainer({ children: renderContainer() }) : renderContainer()}
    </div>
  );
};

export default DateTimePicker;
